import { sha256Hex } from "@opulancia/deposit-core";
import type { StudioBackend } from "./backend.js";
import type { ImageRef } from "./model.js";
import type { LoadedImage } from "./render.js";

/**
 * Images of the Atelier. An imported picture is decoded (orientation applied), reduced to
 * what a 1080 × 1350 slide needs with room to zoom 1.5×, and re-encoded as JPEG, which also
 * drops its metadata (place, device). The stored file is addressed by its sha256: the same
 * picture is stored once, whatever the number of slides or projects using it.
 */
export const COVER_TARGET = { width: 1620, height: 2025 };
export const JPEG_QUALITY = 0.88;
const ACCEPTED = /^image\/(jpeg|png|webp|gif|avif|heic|heif)$/;

export interface PreparedImage {
  ref: ImageRef;
  blob: Blob;
  image: LoadedImage;
}

async function encode(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("Encodage JPEG impossible."))), "image/jpeg", JPEG_QUALITY));
}

export async function prepareImage(file: Blob): Promise<PreparedImage> {
  if (file.type && !ACCEPTED.test(file.type)) throw new Error(`Format non pris en charge (${file.type}). Utilise un JPEG, un PNG ou un WebP.`);
  let decoded: ImageBitmap;
  try {
    decoded = await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch {
    throw new Error("Image illisible par ce navigateur (format HEIC ? exporte-la en JPEG).");
  }
  const scale = Math.min(1, Math.max(COVER_TARGET.width / decoded.width, COVER_TARGET.height / decoded.height));
  const width = Math.max(1, Math.round(decoded.width * scale));
  const height = Math.max(1, Math.round(decoded.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d")!;
  ctx.fillStyle = "#0A0A0A"; // transparent areas become the slide's background
  ctx.fillRect(0, 0, width, height);
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(decoded, 0, 0, width, height);
  decoded.close();
  const blob = await encode(canvas);
  const sha256 = await sha256Hex(new Uint8Array(await blob.arrayBuffer()));
  const image = await createImageBitmap(blob);
  return { ref: { sha256, type: "image/jpeg", width, height }, blob, image: { source: image, width, height } };
}

/** Loaded images of the open project, and the uploads still running. */
export class ImageStore {
  private readonly loaded = new Map<string, LoadedImage>();
  private readonly loading = new Map<string, Promise<LoadedImage>>();
  private readonly uploads = new Map<string, Promise<void>>();
  private readonly blobs = new Map<string, Blob>();
  /** Images whose upload failed: retried by flush(). */
  readonly failed = new Set<string>();

  constructor(private readonly backend: StudioBackend) {}

  get images(): Map<string, LoadedImage> {
    return this.loaded;
  }

  get pending(): number {
    return this.uploads.size + this.failed.size;
  }

  /** A new picture: usable at once, uploaded in the background. */
  add(prepared: PreparedImage): void {
    const sha = prepared.ref.sha256;
    this.loaded.set(sha, prepared.image);
    this.blobs.set(sha, prepared.blob);
    this.upload(sha);
  }

  blob(sha256: string): Blob | undefined {
    return this.blobs.get(sha256);
  }

  private upload(sha: string): Promise<void> {
    const blob = this.blobs.get(sha)!;
    this.failed.delete(sha);
    const running = this.backend.putImage(sha, blob).then(
      () => void this.uploads.delete(sha),
      (error) => {
        this.uploads.delete(sha);
        this.failed.add(sha);
        throw error;
      },
    );
    this.uploads.set(sha, running);
    running.catch(() => {});
    return running;
  }

  /** Waits for every upload, retrying the failed ones; rejects if one still fails. */
  async flush(): Promise<void> {
    for (const sha of [...this.failed]) void this.upload(sha);
    await Promise.all([...this.uploads.values()]);
  }

  /** An image of the project, downloaded once. */
  load(ref: ImageRef): Promise<LoadedImage> {
    const known = this.loaded.get(ref.sha256);
    if (known) return Promise.resolve(known);
    let running = this.loading.get(ref.sha256);
    if (!running) {
      running = (async () => {
        const blob = await this.backend.getImage(ref.sha256);
        const bitmap = await createImageBitmap(blob);
        const image = { source: bitmap, width: bitmap.width, height: bitmap.height };
        this.loaded.set(ref.sha256, image);
        this.blobs.set(ref.sha256, blob);
        return image;
      })();
      this.loading.set(ref.sha256, running);
      running.finally(() => this.loading.delete(ref.sha256)).catch(() => {});
    }
    return running;
  }
}
